/**
 * 商品明细
 */
import React, { memo, useRef } from 'react';
import { connect } from 'react-redux';
import { Table, Button } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import intl from 'react-intl-universal';

import { parseYuan2Currency } from '@/utils/number';
import ContentBlock from '@/components/Reservation/ContentBlock';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import { handleGoodsItemChange } from '@/redux/actions/reversal/goods';
import { ReducerState, GoodsInfo, storeName } from '@/redux/reducers/reversal/goods';
import { BillItem } from '@/pages/bill/interface';
import DiffContent from '@/components/Order/DiffContent';
import GoodsRefundModal, { GoodsRefundModalRef } from '@/components/Order/GoodsRefundModal';
import { isEqualVal } from '@/components/Order/GoodsRefundModal/helper';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { goodsList } = state;
  return {
    goodsList,
  };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callItemChange: handleGoodsItemChange,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface DataListProps extends MapStateProps, MapDispatchProps {
  bill: BillItem;
}

const Index: React.FC<DataListProps> = (props) => {
  const { bill, goodsList, callItemChange } = props;
  const refundModalRef = useRef<GoodsRefundModalRef>(null);

  // 显示编辑弹框
  const showEditModal = (record) => {
    refundModalRef.current.showModal(record);
  };

  // 商品修改
  const handleItemChange = (data: Partial<GoodsInfo>) => {
    callItemChange(data.indexId, data);
  };

  // 撤销修改
  const handleReverseItem = (record: GoodsInfo) => {
    callItemChange(record.indexId, {
      currentCount: record.initCount,
      currentTotalValue: record.initTotalValue,
    });
  };

  // 价格
  const renderPrice = (val) => {
    return parseYuan2Currency(val);
  };

  // 标准售价
  const renderStandardPrice = (val) => {
    if (val) return renderPrice(val);
    return '-';
  };

  // 数量
  const renderCount = (record: GoodsInfo) => {
    return <DiffContent preValue={record.initCount} currentValue={record.currentCount} />;
  };

  // 价格
  const renderTotalValue = (record: GoodsInfo) => {
    return (
      <DiffContent
        preValue={renderPrice(record.initTotalValue)}
        currentValue={renderPrice(record.currentTotalValue)}
      />
    );
  };

  // 操作按钮
  const renderAction = (record: GoodsInfo) => {
    const { initCount, initTotalValue, currentCount, currentTotalValue } = record;
    if (
      bill.status === 0 &&
      record.initCount !== '0' &&
      isEqualVal(initCount, currentCount) &&
      isEqualVal(initTotalValue, currentTotalValue)
    ) {
      return (
        <Button type="link" className={styles.operateButton} onClick={() => showEditModal(record)}>
          {intl.get('reversal_goods_list_table_content_edit')}
        </Button>
      );
    }
    if (
      bill.status === 0 &&
      (!isEqualVal(initCount, currentCount) || !isEqualVal(initTotalValue, currentTotalValue))
    ) {
      return (
        <Button
          type="link"
          className={styles.operateButton}
          onClick={() => handleReverseItem(record)}
        >
          {intl.get('reversal_goods_list_table_content_reverse')}
        </Button>
      );
    }
    return '-';
  };

  const tableColumns: ColumnsType<GoodsInfo> = [
    { title: intl.get('reversal_goods_list_table_column_name'), dataIndex: 'name', key: 'name' },
    {
      title: intl.get('reversal_goods_list_table_column_spec'),
      dataIndex: 'specification',
      key: 'specification',
    },
    {
      title: intl.get('reversal_goods_list_table_column_std'),
      dataIndex: 'standardPrice',
      key: 'standardPrice',
      render: renderStandardPrice,
    },
    {
      title: intl.get('reversal_goods_list_table_column_sale'),
      dataIndex: 'price',
      key: 'price',
      render: renderPrice,
    },
    {
      title: intl.get('reversal_goods_list_table_column_count'),
      key: 'initCount',
      width: 180,
      render: renderCount,
    },
    {
      title: intl.get('reversal_goods_list_table_column_total'),
      key: 'initTotalValue',
      width: 180,
      render: renderTotalValue,
    },
    {
      title: intl.get('app_table_column_action'),
      key: 'operator',
      width: 100,
      align: 'right',
      render: renderAction,
    },
  ];

  return (
    <ContentBlock title={intl.get('reversal_goods_list_block_title')}>
      <Table
        scroll={{ x: 600 }}
        pagination={false}
        className={styles.rentRoomContainer}
        rowKey="indexId"
        columns={tableColumns}
        dataSource={goodsList}
      />
      <GoodsRefundModal ref={refundModalRef} scene="GOODS_REVERSAL" onConfirm={handleItemChange} />
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
